import { useState, useRef, useCallback, useEffect } from "react";
import { useForm } from "react-hook-form";
import type { PointOfInterest } from "../../../../../../store/admin/pointOfInterest";
import type { PointOfInterestFormValues, MapPoint } from "../types";

const defaultValues: PointOfInterestFormValues = {
  name: "",
  latitude: 0,
  longitude: 0,
  address: "",
  shared: false,
  status_id: "",
  category_id: "",
  notes: "",
};

const defaultMapCenter: MapPoint = {
  lat: 19.4326,
  lng: -99.1332,
};

interface UsePointOfInterestFormProps {
  isEdit: boolean;
  editPOI: PointOfInterest | null;
}

export function usePointOfInterestForm({ isEdit, editPOI }: UsePointOfInterestFormProps) {
  const isEditing = useRef(false);
  const [mapCenter, setMapCenter] = useState<MapPoint>(defaultMapCenter);
  const [markerPosition, setMarkerPosition] = useState<MapPoint | null>(null);

  const form = useForm<PointOfInterestFormValues>({
    defaultValues,
    mode: "onChange",
  });

  const { watch, setValue, reset } = form;

  // Verificar si hay cambios reales
  const hasRealChanges = useCallback(() => {
    if (!isEdit || !editPOI) return false;

    const currentValues = watch();

    const basicFieldsChanged =
      currentValues.name !== editPOI.name ||
      currentValues.address !== (editPOI.address || "") ||
      currentValues.shared !== editPOI.shared ||
      currentValues.status_id !== editPOI.status_id ||
      currentValues.category_id !== (editPOI.category_id || "") ||
      currentValues.notes !== (editPOI.notes || "");

    const locationChanged =
      currentValues.latitude !== editPOI.location?.latitude ||
      currentValues.longitude !== editPOI.location?.longitude;

    return basicFieldsChanged || locationChanged;
  }, [isEdit, editPOI, watch]);

  // Manejar cambio de posición del marcador
  const handleMarkerPositionChange = useCallback(
    (position: MapPoint) => {
      setMarkerPosition(position);
      setValue("latitude", position.lat);
      setValue("longitude", position.lng);
    },
    [setValue]
  );

  // Manejar cambio manual de coordenadas
  const handleCoordinateChange = useCallback(
    (lat: number, lng: number) => {
      const newPosition = { lat, lng };
      setMarkerPosition(newPosition);
      setMapCenter(newPosition);
      setValue("latitude", lat);
      setValue("longitude", lng);
    },
    [setValue]
  );

  // Resetear formulario
  const resetForm = useCallback(() => {
    setMapCenter(defaultMapCenter);
    setMarkerPosition(null);
    reset(defaultValues);
    isEditing.current = false;
  }, [reset]);

  // Preparar datos para submit
  const preparePOIData = useCallback(
    (data: PointOfInterestFormValues) => {
      return {
        name: data.name,
        status_id: data.status_id,
        location: {
          latitude: data.latitude,
          longitude: data.longitude,
        },
        address: data.address || undefined,
        shared: data.shared,
        category_id: data.category_id || undefined,
        notes: data.notes || undefined,
      };
    },
    []
  );

  // Efecto para cargar datos de edición
  useEffect(() => {
    if (isEdit && editPOI) {
      isEditing.current = true;

      setValue("name", editPOI.name);
      setValue("status_id", editPOI.status_id);
      setValue("address", editPOI.address || "");
      setValue("shared", editPOI.shared);
      setValue("category_id", editPOI.category_id || "");
      setValue("notes", editPOI.notes || "");

      // Cargar ubicación
      if (editPOI.location) {
        const position = {
          lat: editPOI.location.latitude,
          lng: editPOI.location.longitude,
        };
        setValue("latitude", editPOI.location.latitude);
        setValue("longitude", editPOI.location.longitude);
        setMarkerPosition(position);
        setMapCenter(position);
      }
    }
  }, [isEdit, editPOI, setValue]);

  return {
    form,
    mapCenter,
    setMapCenter,
    markerPosition,
    setMarkerPosition,
    hasRealChanges,
    handleMarkerPositionChange,
    handleCoordinateChange,
    resetForm,
    preparePOIData,
  };
}
